import { useMemo, useState } from 'react';
import {
  Workflow as WorkflowIcon,
  Search,
  Plus,
  Play,
  Pencil,
  Copy,
  Trash2,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@easyflow/ui-frontend/components/ui/table';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@easyflow/ui-frontend/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import { Skeleton } from '@easyflow/ui-frontend/components/ui/skeleton-loader';
import { aurora } from '@/components/aurora/tokens';
import {
  formatTriggerLabel,
  triggerBadgeClass,
  formatRelative,
  formatAbsolute,
} from './workflow-shared';

import type { WorkflowListItem } from '@easyflow/shared';
export type WorkflowsListDialogItem = Pick<WorkflowListItem, 'id' | 'name' | 'description' | 'trigger_type' | 'is_active' | 'version' | 'updated_at' | 'capabilities' | 'admission_scope' | 'managed_by_org_id'>;

interface WorkflowsListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflows: WorkflowsListDialogItem[];
  loading?: boolean;
  runningId?: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRun: (id: string) => void;
  onDuplicate: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onEditWithAI?: (id: string) => void;
  onCreateWithAI?: () => void;
  canCreate: boolean;
}

export function WorkflowsListDialog({
  open,
  onOpenChange,
  workflows,
  loading = false,
  runningId = null,
  onSelect,
  onNew,
  onRun,
  onDuplicate,
  onDelete,
  onEditWithAI,
  onCreateWithAI,
  canCreate,
}: WorkflowsListDialogProps) {
  const [search, setSearch] = useState('');
  const [triggerFilter, setTriggerFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const triggerOptions = useMemo(() => {
    const set = new Set<string>();
    workflows.forEach(w => set.add(w.trigger_type));
    return Array.from(set);
  }, [workflows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workflows.filter(w => {
      if (q && !w.name.toLowerCase().includes(q) && !(w.description || '').toLowerCase().includes(q)) {
        return false;
      }
      if (triggerFilter !== 'all' && w.trigger_type !== triggerFilter) return false;
      if (statusFilter === 'active' && !w.is_active) return false;
      if (statusFilter === 'inactive' && w.is_active) return false;
      return true;
    });
  }, [workflows, search, triggerFilter, statusFilter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[min(95vw,1280px)] max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <WorkflowIcon className="h-5 w-5 text-primary" />
                I miei workflow
                <Badge variant="secondary" className="ml-1 font-normal">
                  {workflows.length}
                </Badge>
              </DialogTitle>
              <DialogDescription className="mt-1">
                Apri un workflow esistente o creane uno nuovo.
              </DialogDescription>
            </div>
            <Button size="sm" disabled={!canCreate} onClick={onNew} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Nuovo workflow
            </Button>
          </div>
        </DialogHeader>

        <div className="px-6 py-3 border-b bg-muted/30 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              aria-label="Cerca workflow"
              placeholder="Cerca per nome o descrizione…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          <Select value={triggerFilter} onValueChange={setTriggerFilter}>
            <SelectTrigger className="h-9 w-[200px]">
              <SelectValue placeholder="Filtra per trigger" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i trigger</SelectItem>
              {triggerOptions.map(t => (
                <SelectItem key={t} value={t}>
                  {formatTriggerLabel(t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="inline-flex rounded-md border bg-background p-0.5">
            {(['all', 'active', 'inactive'] as const).map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${
                  statusFilter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {s === 'all' ? 'Tutti' : s === 'active' ? 'Attivi' : 'Disattivi'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <WorkflowIcon className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-1">
                {workflows.length === 0
                  ? 'Nessun workflow ancora'
                  : 'Nessun risultato'}
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm mb-5">
                {workflows.length === 0
                  ? 'Inizia creando il primo workflow per automatizzare un flusso di lavoro.'
                  : 'Prova a modificare i filtri o la ricerca.'}
              </p>
              {workflows.length === 0 && (
                <Button disabled={!canCreate} onClick={onNew} className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  Crea il primo workflow
                </Button>
              )}
            </div>
          ) : (
            <TooltipProvider delayDuration={200}>
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="min-w-[280px]">Nome</TableHead>
                    <TableHead className="w-[180px]">Trigger</TableHead>
                    <TableHead className="w-[80px]">Versione</TableHead>
                    <TableHead className="w-[140px]">Aggiornato</TableHead>
                    <TableHead className="w-[120px]">Stato</TableHead>
                    <TableHead className="w-[220px] text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((wf) => {
                    const isRunning = runningId === wf.id;
                    return (
                      <TableRow
                        key={wf.id}
                        className="cursor-pointer"
                        onClick={() => onSelect(wf.id)}
                      >
                        <TableCell className="py-3">
                          <div className="flex flex-col min-w-0 max-w-[480px]">
                            <span className="font-medium text-sm break-words leading-snug" title={wf.name}>
                              {wf.name}
                            </span>
                            {wf.description && (
                              <span
                                className="text-xs text-muted-foreground truncate"
                                title={wf.description}
                              >
                                {wf.description}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`text-[10px] px-2 py-0.5 font-medium ${triggerBadgeClass(wf.trigger_type)}`}
                          >
                            {formatTriggerLabel(wf.trigger_type)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-[10px] px-1.5">
                            v{wf.version}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-xs text-muted-foreground">
                                {formatRelative(wf.updated_at)}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{formatAbsolute(wf.updated_at)}</TooltipContent>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {wf.is_active ? (
                            <Badge className="text-[10px] px-2 py-0.5 bg-success/10 text-success border-success/20">
                              Definizione attiva
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] px-2 py-0.5 text-muted-foreground">
                              Definizione disabilitata
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-success hover:text-success hover:bg-success/10 disabled:opacity-40"
                                  disabled={!wf.is_active || isRunning}
                                  aria-label={`Esegui la versione salvata di ${wf.name}`}
                                  onClick={() => onRun(wf.id)}
                                >
                                  {isRunning ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Esegui la versione salvata (effetti reali)
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  aria-label={`Apri ${wf.name} nel Builder`}
                                  onClick={() => onSelect(wf.id)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Modifica nel canvas</TooltipContent>
                            </Tooltip>
                            {onEditWithAI && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className={`h-8 w-8 ${aurora.accent.ai} hover:bg-accent`}
                                    disabled={!wf.capabilities.can_edit_definition}
                                    aria-label={`Modifica con AI ${wf.name}`}
                                    onClick={() => onEditWithAI(wf.id)}
                                  >
                                    <Sparkles className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Modifica con AI</TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  onClick={() => onDuplicate(wf.id, `${wf.name} (copia)`)}
                                  disabled={!wf.capabilities.can_clone_editable}
                                  aria-label={`Duplica ${wf.name}`}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Duplica</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => onDelete(wf.id)}
                                  disabled={!wf.capabilities.can_delete_definition}
                                  aria-label={`Elimina ${wf.name}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Elimina</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TooltipProvider>
          )}
        </div>

        {onCreateWithAI && (
          <div className="px-6 py-3 border-t bg-muted/20 flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={onCreateWithAI}
              disabled={!canCreate}
              className={`gap-1.5 ${aurora.accent.ai} hover:bg-accent`}
            >
              <Sparkles className="h-4 w-4" />
              Crea con AI
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
